import {
  ApprovalDecisionWsPayload,
  ApprovalRequestExpiredWsPayload,
  ApprovalRequestedWsPayload,
  ApprovalStageCompletedWsPayload,
  KillSwitchExecutedWsPayload,
  KillSwitchReleasedWsPayload,
  PolicyEventWsPayload,
  PolicyRequestedWsPayload,
  SigningProgressUpdatedWsPayload,
  TransactionFailedWsPayload,
  TransactionFinalizedWsPayload,
  VaultStatusWsPayload,
  VaultTotalBalanceUpdatedWsPayload,
  WalletBalanceUpdatedWsPayload,
  WalletCreatedWsPayload,
  WalletStatusChangedWsPayload,
  WalletUpdatedWsPayload,
  WithdrawalOperationAddedWsPayload,
  WithdrawalOperationUpdatedWsPayload,
  WsEnvelope,
  WsEventType,
} from '@app/core/realtime/realtime.types';
import {
  InAppNotificationItemDto,
  NotificationItem,
} from '@shared/models/in-app-notification.model';

/**
 * Whitelist of WS event types that surface in the bell. Anything not in this
 * set is ignored — keeping the inbox focused on items users actually act on.
 */
const USER_FACING_EVENTS: ReadonlySet<WsEventType> = new Set([
  WsEventType.USER_ONBOARDED,
  WsEventType.USER_ROLE_CHANGED,
  WsEventType.USER_DELETED,
  WsEventType.USER_ACCOUNT_FROZEN,
  WsEventType.USER_ACCOUNT_UNFROZEN,
  WsEventType.USER_ACCOUNT_BLOCKED,
  WsEventType.USER_ACCOUNT_UNBLOCKED,
  WsEventType.ACCOUNT_FROZEN,
  WsEventType.ACCOUNT_UNFROZEN,
  WsEventType.TOKEN_REUSE_DETECTED,
  WsEventType.GOOGLE_AUTH_SETUP_COMPLETED,
  WsEventType.GOOGLE_AUTH_DISABLED,
  WsEventType.EMAIL_CHANGED,
  WsEventType.USER_PASSWORD_RESET,
  WsEventType.ACCOUNT_BLOCKED,
  WsEventType.ACCOUNT_UNBLOCKED,
  WsEventType.VAULT_ENABLED,
  WsEventType.VAULT_DISABLED,
  WsEventType.VAULT_TOTAL_BALANCE_UPDATED,
  // Account-aggregate companion to VAULT_TOTAL_BALANCE_UPDATED. The
  // dispatcher already routes it to `loadVaults`; surfacing it on the
  // bell keeps the user aware of cross-vault balance moves they didn't
  // initiate (the per-vault event alone doesn't cover the
  // account-totals widget the backend marks as the preferred source).
  WsEventType.ACCOUNT_TOTAL_BALANCE_UPDATED,
  WsEventType.WALLET_CREATED,
  WsEventType.WALLET_UPDATED,
  WsEventType.WALLET_STATUS_CHANGED,
  WsEventType.WALLET_BALANCE_UPDATED,
  WsEventType.WHITELIST_ADDRESS_CREATED,
  WsEventType.WHITELIST_ADDRESS_ENABLED,
  WsEventType.WHITELIST_ADDRESS_DISABLED,
  WsEventType.WHITELIST_ADDRESS_DELETED,
  WsEventType.WITHDRAWAL_OPERATION_ADDED,
  WsEventType.WITHDRAWAL_OPERATION_UPDATED,
  WsEventType.TRANSACTION_FINALIZED,
  WsEventType.TRANSACTION_FAILED,
  WsEventType.SIGNING_PROGRESS_UPDATED,
  // NOTE: BLOCKCHAIN_CONFIRMATIONS_UPDATED is intentionally *not* user-facing.
  // It fires once per on-chain confirmation increment (often 6–12 events per
  // transaction) and would swamp the bell with effectively duplicate rows.
  // The data slice still consumes it via the dispatcher's silent refresh
  // of step-4 blockchain-details.
  WsEventType.POLICY_CREATED,
  WsEventType.POLICY_UPDATED,
  WsEventType.POLICY_DELETED,
  WsEventType.POLICY_ACTIVATED,
  WsEventType.POLICY_DEACTIVATED,
  WsEventType.POLICY_CREATE_REQUESTED,
  WsEventType.POLICY_UPDATE_REQUESTED,
  WsEventType.POLICY_ACTIVATE_REQUESTED,
  WsEventType.POLICY_DEACTIVATE_REQUESTED,
  WsEventType.POLICY_DELETE_REQUESTED,
  WsEventType.APPROVAL_REQUESTED,
  WsEventType.APPROVAL_APPROVED,
  WsEventType.APPROVAL_REJECTED,
  WsEventType.APPROVAL_CANCELED,
  WsEventType.APPROVAL_STAGE_COMPLETED,
  WsEventType.APPROVAL_REQUEST_EXPIRED,
  WsEventType.KILL_SWITCH_EXECUTED,
  WsEventType.KILL_SWITCH_RELEASED,
]);

export function isUserFacingEvent(type: WsEventType): boolean {
  return USER_FACING_EVENTS.has(type);
}

/**
 * Pure mapping: envelope → bell entry. Returns `null` for envelopes that
 * have no UI value (handshake/peer-device events, internal forwards).
 *
 * `id` is deterministic enough for de-dup (correlationId if available,
 * otherwise type + occurredAt + a payload-derived suffix) so the same event
 * delivered twice does not produce two bell rows.
 *
 * `title` and `message` are passed through verbatim — both are
 * server-pre-localized per the WEBSOCKET-EVENTS.md envelope contract.
 */
export function buildNotification(
  envelope: WsEnvelope
): NotificationItem | null {
  if (!isUserFacingEvent(envelope.type)) {
    return null;
  }

  const navigateTo = computeNavigateTo(envelope);
  const occurredAt = resolveOccurredAt(envelope);
  const correlationId = normalizeCorrelationId(envelope.correlationId);
  const notificationId = extractPersistedNotificationId(envelope);
  const clientKey =
    notificationId ?? computeId(envelope, occurredAt, correlationId);

  return {
    id: notificationId ?? clientKey,
    clientKey,
    notificationId,
    type: envelope.type,
    title: envelope.title,
    message: envelope.message,
    occurredAt,
    payload: envelope.payload,
    read: false,
    navigateTo,
    correlationId,
  };
}

/**
 * Maps a persisted in-app notification from notification-management REST
 * into the bell's `NotificationItem`. Uses the server UUID as `id` so
 * mark-read can round-trip to `POST /api/notifications/read`.
 *
 * Non-user-facing types are still mapped (the server already filtered the
 * inbox); navigateTo is computed the same way as live WS rows.
 */
export function mapInAppNotificationToItem(
  dto: InAppNotificationItemDto
): NotificationItem {
  const type = dto.type as WsEventType;
  const envelope: WsEnvelope = {
    type,
    payload: dto.payload ?? {},
    title: dto.title,
    message: dto.message,
    occurredAt: dto.occurredAt,
    correlationId: normalizeCorrelationId(dto.correlationId),
  };

  return {
    id: dto.id,
    clientKey: dto.id,
    notificationId: dto.id,
    type,
    title: dto.title,
    message: dto.message,
    occurredAt: dto.occurredAt,
    payload: dto.payload ?? {},
    read: dto.isRead,
    navigateTo: computeNavigateTo(envelope),
    correlationId: envelope.correlationId,
  };
}

export function normalizeCorrelationId(
  value: string | Record<string, unknown> | null | undefined
): string | undefined {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['id', 'value', 'correlationId', 'correlation_id']) {
      const nested = obj[key];
      if (typeof nested === 'string' && nested.length > 0) {
        return nested;
      }
    }
    const stringValues = Object.values(obj).filter(
      (entry): entry is string => typeof entry === 'string' && entry.length > 0
    );
    if (stringValues.length === 1) {
      return stringValues[0];
    }
  }
  return undefined;
}

function normalizeOccurredAt(value: string | undefined): string {
  if (!value) {
    return '';
  }
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return value.trim();
  }
  return new Date(parsed).toISOString().slice(0, 19);
}

function occurredAtClose(
  a: string | undefined,
  b: string | undefined,
  toleranceMs = 5000
): boolean {
  if (!a || !b) {
    return false;
  }
  const da = Date.parse(a);
  const db = Date.parse(b);
  if (Number.isNaN(da) || Number.isNaN(db)) {
    return normalizeOccurredAt(a) === normalizeOccurredAt(b);
  }
  return Math.abs(da - db) <= toleranceMs;
}

function correlationIdsEqual(
  a: string | undefined,
  b: string | undefined
): boolean {
  if (!a || !b) {
    return false;
  }
  if (a === b) {
    return true;
  }
  return normalizeCorrelationId(a) === normalizeCorrelationId(b);
}

/** True when `id` is a server notification UUID (mark-read API accepts these). */
export function isPersistedNotificationId(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    id
  );
}

/**
 * Read a persisted notification UUID from a WS envelope when the publisher
 * includes one (envelope root or payload).
 */
export function extractPersistedNotificationId(
  envelope: WsEnvelope
): string | undefined {
  const payload = envelope.payload as
    | Record<string, unknown>
    | null
    | undefined;
  // Do not treat payload `id` as a notification UUID — entity payloads
  // (wallet, policy, approval, …) often carry their own UUID under `id`.
  const candidates = [
    envelope.notificationId,
    (envelope as { id?: unknown }).id,
    payload?.['notificationId'],
    payload?.['inAppNotificationId'],
  ];
  for (const raw of candidates) {
    if (typeof raw === 'string' && isPersistedNotificationId(raw)) {
      return raw;
    }
  }
  return undefined;
}

const PAYLOAD_MATCH_KEYS = [
  'withdrawalOperationId',
  'transactionId',
  'requestId',
  'policyId',
  'walletId',
  'vaultId',
  'whitelistAddressId',
  'userId',
  'sessionId',
  'killSwitchOperationId',
  'accountId',
  'milestone',
] as const;

/** Stable fingerprint from entity ids inside the event payload. */
export function payloadEntityFingerprint(payload: unknown): string | null {
  const record = payload as Record<string, unknown> | null | undefined;
  if (!record) {
    return null;
  }
  const parts: string[] = [];
  for (const key of PAYLOAD_MATCH_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) {
      parts.push(`${key}:${value}`);
    }
  }
  return parts.length > 0 ? parts.join('|') : null;
}

/**
 * True when two bell rows describe the same Kafka-sourced event
 * (REST hydrate vs live WS, or dual WS delivery).
 *
 * Matching is intentionally strict: same entity + type alone is not enough,
 * otherwise a later update for the same wallet/policy/approval would be
 * dropped as a duplicate and the bell would stop reflecting new WS events.
 */
export function isSameNotificationEvent(
  a: Pick<
    NotificationItem,
    'type' | 'correlationId' | 'occurredAt' | 'title' | 'message' | 'payload'
  >,
  b: Pick<
    NotificationItem,
    'type' | 'correlationId' | 'occurredAt' | 'title' | 'message' | 'payload'
  >
): boolean {
  if (a.type !== b.type) {
    return false;
  }

  if (correlationIdsEqual(a.correlationId, b.correlationId)) {
    return true;
  }

  const fpA = payloadEntityFingerprint(a.payload);
  const fpB = payloadEntityFingerprint(b.payload);
  if (
    fpA &&
    fpB &&
    fpA === fpB &&
    occurredAtClose(a.occurredAt, b.occurredAt)
  ) {
    return true;
  }

  if (
    a.title &&
    b.title &&
    a.message &&
    b.message &&
    a.title === b.title &&
    a.message === b.message &&
    occurredAtClose(a.occurredAt, b.occurredAt)
  ) {
    return true;
  }

  return false;
}

/** Find a bell row by `clientKey`, `id`, or `notificationId`. */
export function findNotificationItemByKey(
  items: NotificationItem[],
  key: string
): NotificationItem | undefined {
  return items.find(
    item =>
      item.clientKey === key || item.id === key || item.notificationId === key
  );
}

/** Server UUID accepted by `POST /api/notifications/read`, when available. */
export function resolveMarkReadNotificationId(
  item: NotificationItem
): string | null {
  const candidate = item.notificationId ?? item.id;
  return isPersistedNotificationId(candidate) ? candidate : null;
}

/**
 * Find the server UUID for a WS-only row by matching against persisted
 * candidates (in-memory inbox or a REST page).
 */
export function findPersistedNotificationIdForItem(
  item: NotificationItem,
  candidates: NotificationItem[]
): string | null {
  for (const candidate of candidates) {
    if (!isPersistedNotificationId(candidate.id)) {
      continue;
    }
    if (isSameNotificationEvent(item, candidate)) {
      return candidate.id;
    }
  }
  return null;
}

/**
 * Some payloads (e.g. SIGNING_PROGRESS_UPDATED, BLOCKCHAIN_CONFIRMATIONS_UPDATED)
 * also carry `occurredAt` inside the payload. Prefer the envelope-root
 * timestamp when set, and fall back to the payload-level value so the
 * bell's date column never falls back to an empty string.
 */
function resolveOccurredAt(envelope: WsEnvelope): string {
  if (envelope.occurredAt) {
    return envelope.occurredAt;
  }
  const payload = envelope.payload as
    | { occurredAt?: string }
    | null
    | undefined;
  return payload?.occurredAt ?? '';
}

function computeId(
  envelope: WsEnvelope,
  occurredAt: string,
  correlationId?: string
): string {
  const corr = correlationId ?? normalizeCorrelationId(envelope.correlationId);
  if (corr) {
    return `corr:${corr}:${envelope.type}`;
  }
  const suffix =
    extractPayloadKey(envelope) ?? Math.random().toString(36).slice(2, 10);
  return `${envelope.type}:${occurredAt}:${suffix}`;
}

function extractPayloadKey(envelope: WsEnvelope): string | null {
  const fingerprint = payloadEntityFingerprint(envelope.payload);
  if (!fingerprint) {
    return null;
  }
  return fingerprint.split('|')[0] ?? null;
}

/**
 * Routes are intentionally kept in lockstep with `app.routes.ts` — every
 * URL emitted here must resolve to an actual route, otherwise clicking
 * the bell row silently falls through the `**` wildcard and bounces the
 * user back to `/auth`. Confirmed against the dashboard children block
 * (`/dashboard/vaults/:id`, `/dashboard/wallets/:id`, etc.) — there is
 * NO `/dashboard/asset-management/...` or `/dashboard/laws-and-policies/...`
 * prefix in this app despite the folder layout suggesting otherwise.
 */
function computeNavigateTo(envelope: WsEnvelope): string | undefined {
  switch (envelope.type) {
    // ---- Wallet ----
    case WsEventType.WALLET_CREATED: {
      const p = envelope.payload as WalletCreatedWsPayload;
      return p?.walletId
        ? `/dashboard/wallets/${p.walletId}`
        : '/dashboard/wallets';
    }
    case WsEventType.WALLET_UPDATED:
    case WsEventType.WALLET_STATUS_CHANGED:
    case WsEventType.WALLET_BALANCE_UPDATED: {
      const p = envelope.payload as
        | WalletUpdatedWsPayload
        | WalletStatusChangedWsPayload
        | WalletBalanceUpdatedWsPayload;
      return p?.walletId
        ? `/dashboard/wallets/${p.walletId}`
        : '/dashboard/wallets';
    }

    // ---- Vault ----
    case WsEventType.VAULT_ENABLED:
    case WsEventType.VAULT_DISABLED: {
      const p = envelope.payload as VaultStatusWsPayload;
      return p?.vaultId
        ? `/dashboard/vaults/${p.vaultId}`
        : '/dashboard/vaults';
    }
    case WsEventType.VAULT_TOTAL_BALANCE_UPDATED: {
      const p = envelope.payload as VaultTotalBalanceUpdatedWsPayload;
      return p?.vaultId
        ? `/dashboard/vaults/${p.vaultId}`
        : '/dashboard/vaults';
    }
    // Account-aggregate balance update is not scoped to a single vault, so
    // the list is the only sensible landing page.
    case WsEventType.ACCOUNT_TOTAL_BALANCE_UPDATED:
      return '/dashboard/vaults';

    // ---- Whitelist ----
    // There's no per-address detail page; every whitelist event lands on
    // the list. Kept as a single fall-through to avoid the previous
    // dead-conditional that returned the same string from both branches.
    case WsEventType.WHITELIST_ADDRESS_CREATED:
    case WsEventType.WHITELIST_ADDRESS_ENABLED:
    case WsEventType.WHITELIST_ADDRESS_DISABLED:
    case WsEventType.WHITELIST_ADDRESS_DELETED:
      return '/dashboard/whitelist-addresses';

    // ---- Transactions / withdrawals ----
    case WsEventType.WITHDRAWAL_OPERATION_ADDED:
    case WsEventType.WITHDRAWAL_OPERATION_UPDATED: {
      const p = envelope.payload as
        | WithdrawalOperationAddedWsPayload
        | WithdrawalOperationUpdatedWsPayload;
      return p?.withdrawalOperationId
        ? `/dashboard/transactions/${p.withdrawalOperationId}`
        : '/dashboard/transactions';
    }
    case WsEventType.TRANSACTION_FINALIZED:
    case WsEventType.TRANSACTION_FAILED: {
      const p = envelope.payload as
        | TransactionFinalizedWsPayload
        | TransactionFailedWsPayload;
      const id = (p as TransactionFailedWsPayload).withdrawalOperationId;
      return id ? `/dashboard/transactions/${id}` : '/dashboard/transactions';
    }

    // Signing progress points at the same transaction-detail step the user
    // would navigate to on a withdrawal-update toast. `SIGNING_PROGRESS_UPDATED`
    // uses `transactionId` (not `withdrawalOperationId`) as its identifier
    // — same id surface in the backend.
    case WsEventType.SIGNING_PROGRESS_UPDATED: {
      const p = envelope.payload as SigningProgressUpdatedWsPayload;
      return p?.transactionId
        ? `/dashboard/transactions/${p.transactionId}`
        : '/dashboard/transactions';
    }

    // ---- Policy ----
    // The detail route is `policy-detail/:id`, not `policies/:id`. The
    // sidebar's "Policies" entry points at the list at `/dashboard/policies`.
    case WsEventType.POLICY_CREATED:
    case WsEventType.POLICY_UPDATED:
    case WsEventType.POLICY_DELETED:
    case WsEventType.POLICY_ACTIVATED:
    case WsEventType.POLICY_DEACTIVATED: {
      const p = envelope.payload as PolicyEventWsPayload;
      return p?.policyId
        ? `/dashboard/policy-detail/${p.policyId}`
        : '/dashboard/policies';
    }
    case WsEventType.POLICY_CREATE_REQUESTED:
    case WsEventType.POLICY_UPDATE_REQUESTED:
    case WsEventType.POLICY_ACTIVATE_REQUESTED:
    case WsEventType.POLICY_DEACTIVATE_REQUESTED:
    case WsEventType.POLICY_DELETE_REQUESTED: {
      const p = envelope.payload as PolicyRequestedWsPayload;
      return p?.requestId
        ? `/dashboard/approvals/${p.requestId}`
        : '/dashboard/approvals';
    }

    // ---- Approval lifecycle ----
    case WsEventType.APPROVAL_REQUESTED:
    case WsEventType.APPROVAL_APPROVED:
    case WsEventType.APPROVAL_REJECTED:
    case WsEventType.APPROVAL_CANCELED:
    case WsEventType.APPROVAL_STAGE_COMPLETED:
    case WsEventType.APPROVAL_REQUEST_EXPIRED: {
      const p = envelope.payload as
        | ApprovalRequestedWsPayload
        | ApprovalDecisionWsPayload
        | ApprovalStageCompletedWsPayload
        | ApprovalRequestExpiredWsPayload;
      return p?.requestId
        ? `/dashboard/approvals/${p.requestId}`
        : '/dashboard/approvals';
    }

    // ---- Users ----
    // Route id is `/dashboard/user-list`, not `/dashboard/users`.
    case WsEventType.USER_ONBOARDED:
    case WsEventType.USER_ROLE_CHANGED:
    case WsEventType.USER_DELETED:
    case WsEventType.USER_ACCOUNT_FROZEN:
    case WsEventType.USER_ACCOUNT_UNFROZEN:
    case WsEventType.USER_ACCOUNT_BLOCKED:
    case WsEventType.USER_ACCOUNT_UNBLOCKED:
      return '/dashboard/user-list';

    // ---- Auth / security ----
    // Account-settings is a single page (no `/account-security` sub-route).
    // The security tab is selected inside the component; deep-linking to
    // that tab is left as a follow-up if needed.
    case WsEventType.TOKEN_REUSE_DETECTED:
    case WsEventType.USER_PASSWORD_RESET:
    case WsEventType.GOOGLE_AUTH_SETUP_COMPLETED:
    case WsEventType.GOOGLE_AUTH_DISABLED:
    case WsEventType.EMAIL_CHANGED:
      return '/dashboard/account-settings';

    // Kill-switch surfaces on the emergency-control page. There is no
    // `/dashboard/security-settings` route in this app. Payload type
    // narrowing isn't needed for the URL itself, but the cast is left
    // here so future deep-linking by `killSwitchOperationId` has the
    // typed payload ready.
    case WsEventType.KILL_SWITCH_EXECUTED:
    case WsEventType.KILL_SWITCH_RELEASED: {
      const _p = envelope.payload as
        | KillSwitchExecutedWsPayload
        | KillSwitchReleasedWsPayload;
      void _p;
      return '/dashboard/emergency-control';
    }

    case WsEventType.ACCOUNT_FROZEN:
    case WsEventType.ACCOUNT_UNFROZEN:
    case WsEventType.ACCOUNT_BLOCKED:
    case WsEventType.ACCOUNT_UNBLOCKED:
      return '/dashboard/account-settings';

    default:
      return undefined;
  }
}
